import ReconnectingWebSocket from 'reconnecting-websocket';

interface WsRequest {
  id: string;
  route: string;
  body: any;
}

interface WsResponse {
  id: string;
  status: number;
  body?: any;
  error?: string;
}

interface PendingRequest {
  resolve: (value: { data: any; status: number }) => void;
  reject: (reason: any) => void;
  timer: ReturnType<typeof setTimeout>;
}

let requestCounter = 0;

class WebSocketService {
  private ws: ReconnectingWebSocket | null = null;

  private pending: Map<string, PendingRequest> = new Map();

  private timeout: number;

  constructor() {
    this.timeout = 180000; // 180s for WebSocket (no browser timeout issue)
  }

  connect(token: string): Promise<void> {
    const url = `${process.env.REACT_APP_WS_SERVICE_HOST}?token=${encodeURIComponent(token)}`;

    return new Promise((resolve, reject) => {
      this.ws = new ReconnectingWebSocket(url, [], {
        maxRetries: 10,
        connectionTimeout: 5000,
      });

      this.ws.onopen = () => {
        this.ws!.onerror = () => {};
        resolve();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const resp: WsResponse = JSON.parse(event.data);
          const req = this.pending.get(resp.id);
          if (req) {
            clearTimeout(req.timer);
            this.pending.delete(resp.id);

            if (resp.error && resp.status >= 400) {
              req.reject({
                response: {
                  status: resp.status,
                  data: { messagebox: resp.error, titlebox: '오류' },
                },
              });
            } else {
              req.resolve({ data: resp.body, status: resp.status });
            }
          }
        } catch (e) {
          console.error('ws: parse error', e);
        }
      };

      this.ws.onerror = () => {
        // reconnecting-websocket handles reconnection
      };

      this.ws.onclose = () => {
        // Reject all pending requests
        this.pending.forEach((req) => {
          clearTimeout(req.timer);
          req.reject({
            response: {
              status: 503,
              data: { messagebox: '서버 연결이 끊어졌습니다.', titlebox: '연결 오류' },
            },
          });
        });
        this.pending.clear();
      };

      // If first connection fails
      const errHandler = () => {
        reject(new Error('WebSocket connection failed'));
        if (this.ws) this.ws.onerror = () => {};
      };
      this.ws.onerror = errHandler;
    });
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.pending.forEach((req) => {
      clearTimeout(req.timer);
      req.reject({
        response: {
          status: 503,
          data: { messagebox: '연결이 해제되었습니다.', titlebox: '연결 오류' },
        },
      });
    });
    this.pending.clear();
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // eslint-disable-next-line prefer-promise-reject-errors
  send(route: string, body: any, timeout?: number): Promise<{ data: any; status: number }> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          response: {
            status: 503,
            data: { messagebox: 'WebSocket 연결 안됨', titlebox: '연결 오류' },
          },
        });
        return;
      }

      requestCounter += 1;
      const id = `req-${Date.now()}-${requestCounter}`;
      const timeoutMs = timeout ?? this.timeout;

      const timer = setTimeout(() => {
        this.pending.delete(id);
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          response: {
            status: 408,
            data: { messagebox: '요청 시간이 초과되었습니다.', titlebox: '시간초과' },
          },
        });
      }, timeoutMs);

      this.pending.set(id, { resolve, reject, timer });

      const msg: WsRequest = { id, route, body };
      this.ws.send(JSON.stringify(msg));
    });
  }
}

const instance = new WebSocketService();
export default instance;
